import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Award, GraduationCap, Stethoscope, Heart, Users, MapPin } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { SectionHeader } from "../components/SectionHeader";
import { IMAGES } from "../lib/clinic";

const QUALIFICATIONS = [
  { code: "MBBS", title: "Bachelor of Medicine", desc: "Foundational medical degree with clinical training." },
  { code: "MS", title: "Master of Surgery", desc: "Advanced surgical training and specialization." },
  { code: "MCh", title: "Magister Chirurgiae", desc: "Super-specialty training in neurological care." },
  { code: "Psych.", title: "Clinical Psychology", desc: "Trained in modern therapy and counseling techniques." },
];

const EXPERTISE = [
  "Neurological evaluation & diagnosis",
  "Stroke care & rehabilitation",
  "Headache, migraine & vertigo",
  "Movement disorders (Parkinson's)",
  "Memory & cognitive assessment",
  "Anxiety, depression & panic disorders",
  "Sleep & stress management",
  "Patient & family counseling",
];

const About = () => {
  return (
    <div data-testid="about-page">
      <PageHeader
        eyebrow="About Dr. Avril Dhar"
        title="A doctor who treats people — not just patients."
        subtitle="Combining the precision of neuroscience with the warmth of psychology, Dr. Avril Dhar has spent over 15 years helping patients regain control of their health, mood and mind."
        breadcrumbs={["About"]}
      />

      {/* Main profile */}
      <section className="section-y bg-paper">
        <div className="container-x grid lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="absolute -left-4 -top-4 w-24 h-24 border border-brand-goldDark/40 -z-10" />
              <div className="aspect-[4/5] overflow-hidden">
                <img src={IMAGES.doctor_desk} alt="Dr. Avril Dhar at her desk" className="w-full h-full object-cover grayscale-[8%]" />
              </div>
              <p className="mt-4 text-[12px] tracking-[0.18em] uppercase text-brand-goldDark heading-serif italic">
                Personalized patient-focused consultation
              </p>
            </motion.div>
          </div>

          <div className="lg:col-span-7 space-y-7 lg:pt-8">
            <div className="eyebrow">Profile</div>
            <h2 className="heading-serif text-4xl md:text-5xl lg:text-[3.5rem] font-medium text-brand-navyDark leading-[1.08]">
              Where neurology meets psychology — for whole-person care.
            </h2>
            <p className="text-brand-body leading-[1.85] text-[15px]">
              Dr. Avril Dhar is a respected Neurologist and Psychologist based in Chandigarh,
              India, with over 15 years of clinical experience across government hospitals
              and private consultation. Her dual training allows her to look beyond a single
              symptom — and treat the brain and mind together, the way they actually work.
            </p>
            <p className="text-brand-body leading-[1.85] text-[15px]">
              From migraines and memory concerns to anxiety, stress and sleep — patients come
              to her for clarity, patient listening and a calm, structured plan. She believes
              every patient deserves to be heard, fully understood, and given a treatment
              plan that fits their real life.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-5 pt-6 border-t border-stone-200">
              {[
                { icon: Award, label: "15+ Years", sub: "Experience" },
                { icon: Users, label: "1000+", sub: "Patients" },
                { icon: MapPin, label: "Chandigarh", sub: "Based" },
                { icon: Heart, label: "Empathic", sub: "Approach" },
              ].map((s, i) => (
                <div key={i}>
                  <s.icon className="w-4 h-4 text-brand-tealDark mb-3" strokeWidth={1.5} />
                  <div className="heading-serif text-xl text-brand-navyDark font-medium leading-tight">{s.label}</div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-brand-muted mt-1">{s.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Qualifications */}
      <section className="section-y bg-paper-soft">
        <div className="container-x">
          <SectionHeader
            eyebrow="Qualifications"
            title="Trained at top institutions"
            subtitle="A foundation built on rigorous medical training and continuous learning."
            align="left"
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-stone-200 mt-16">
            {QUALIFICATIONS.map((q, i) => (
              <motion.div
                key={q.code}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="bg-paper-soft p-9 md:p-10 group hover:bg-paper transition-colors"
                data-testid={`qualification-${q.code}`}
              >
                <GraduationCap className="w-6 h-6 text-brand-tealDark mb-7" strokeWidth={1.3} />
                <div className="heading-serif text-4xl text-brand-navyDark font-medium mb-2">
                  {q.code}
                </div>
                <div className="text-[12px] uppercase tracking-[0.18em] text-brand-goldDark mb-4">{q.title}</div>
                <p className="text-[14px] text-brand-body leading-[1.75]">{q.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Areas of expertise */}
      <section className="section-y bg-paper">
        <div className="container-x grid lg:grid-cols-12 gap-14 items-start">
          <motion.div
            initial={{ opacity: 0, x: -16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <div className="aspect-[4/5] overflow-hidden">
              <img src={IMAGES.doctor_podium} alt="Dr. Avril speaking at a public event" className="w-full h-full object-cover grayscale-[8%]" />
            </div>
            <p className="mt-4 text-[12px] tracking-[0.18em] uppercase text-brand-goldDark heading-serif italic">
              Public health awareness and education
            </p>
          </motion.div>
          <div className="lg:col-span-6 lg:col-start-7 lg:pt-8">
            <div className="eyebrow mb-5">Areas of Expertise</div>
            <h2 className="heading-serif text-4xl md:text-5xl font-medium text-brand-navyDark leading-[1.08] mb-7">
              Deep expertise across two specialties.
            </h2>
            <p className="text-brand-body leading-[1.85] mb-9 text-[15px]">
              From routine consultations to complex cases, Dr. Avril offers structured
              evaluation, modern treatment protocols, and on-going emotional support.
            </p>
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-1">
              {EXPERTISE.map((e) => (
                <li
                  key={e}
                  className="flex items-start gap-3 py-3 border-b border-stone-200"
                >
                  <span className="text-brand-goldDark text-xs mt-2">—</span>
                  <span className="text-[14px] text-brand-navyDark">{e}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Hospital background */}
      <section className="section-y bg-brand-navyDark text-white">
        <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <Stethoscope className="w-7 h-7 text-brand-gold mb-7" strokeWidth={1.3} />
            <h3 className="heading-serif text-4xl md:text-5xl font-medium text-white leading-[1.08] mb-6">
              Government hospital background.
            </h3>
            <p className="text-stone-300 leading-[1.85] mb-4 text-[15px]">
              Trained and seasoned in high-volume government hospital settings, Dr. Avril has
              managed thousands of complex neurological cases across stroke, epilepsy,
              movement disorders and emergency neurology.
            </p>
            <p className="text-stone-300 leading-[1.85] text-[15px]">
              That hands-on experience translates directly into private practice — sharper
              diagnosis, careful prescribing and confident handling of unusual presentations.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-px bg-white/10">
            {[
              { num: "10,000+", label: "Patients Examined" },
              { num: "2,000+", label: "Stroke Consults" },
              { num: "500+", label: "Migraine Plans" },
              { num: "100%", label: "Confidentiality" },
            ].map((s) => (
              <div key={s.label} className="bg-brand-navyDark p-8 md:p-10">
                <div className="heading-serif text-4xl md:text-5xl text-white font-medium">{s.num}</div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-brand-gold mt-3">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-y bg-paper">
        <div className="container-x max-w-3xl">
          <h2 className="heading-serif text-4xl md:text-5xl font-medium text-brand-navyDark mb-6 leading-[1.08]">
            Ready to consult Dr. Avril?
          </h2>
          <p className="text-brand-body mb-10 text-[16px] leading-[1.8]">
            Book an appointment in clinic or online — get clarity, care and a plan that works.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/contact#book" className="btn-primary" data-testid="about-book-btn">
              Book Appointment
            </Link>
            <Link to="/neurology" className="btn-secondary">View Services</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
